import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import './Input.css';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputStatus = 'default' | 'error' | 'success';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Текст метки над полем */
  label?: string;
  /** Подсказка/описание под полем (или текст ошибки если status='error') */
  hint?: string;
  /** Статус валидации — меняет рамку и подсказку */
  status?: InputStatus;
  /** Иконка слева внутри поля */
  leftIcon?: ReactNode;
  /** Иконка справа внутри поля (для clear/show password и т.п.) */
  rightIcon?: ReactNode;
  /** Размер */
  size?: InputSize;
  /** Растянуть на 100% ширины */
  fullWidth?: boolean;
}

/**
 * Поле ввода с метками, валидацией, иконками.
 *
 * @example
 *   <Input label="Email" type="email" status="error" hint="Неверный формат" />
 *   <Input leftIcon={<IoSearch />} placeholder="Поиск" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, status = 'default', leftIcon, rightIcon, size = 'md', fullWidth = false, id, className, disabled, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;

  const wrapperCls = [
    'ds-input',
    `ds-input--${size}`,
    fullWidth ? 'ds-input--block' : '',
    status !== 'default' ? `ds-input--${status}` : '',
    disabled ? 'is-disabled' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperCls}>
      {label && <label className="ds-input__label" htmlFor={inputId}>{label}</label>}
      <div className="ds-input__control">
        {leftIcon && <span className="ds-input__icon ds-input__icon--left" aria-hidden="true">{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          className="ds-input__field"
          disabled={disabled}
          aria-invalid={status === 'error' || undefined}
          aria-describedby={hint ? hintId : undefined}
          {...rest}
        />
        {rightIcon && <span className="ds-input__icon ds-input__icon--right" aria-hidden="true">{rightIcon}</span>}
      </div>
      {hint && (
        <div id={hintId} className="ds-input__hint">{hint}</div>
      )}
    </div>
  );
});
